<template>
  <div class="design-terminal">
    <front-page-design-nav theme="dark" />

    <section class="hero">
      <div class="hero__left">
        <img :src="logo" alt="BOX21" class="hero__logo" />
        <pre class="hero__code"><span class="comment"># vision pipeline</span>
<span class="keyword">box21</span> init --project wildlife
<span class="keyword">box21</span> annotate --mode boxes,keypoints
<span class="keyword">box21</span> train --arch yolov9 --epochs 100
<span class="keyword">box21</span> deploy --target edge,mobile

<span class="output">→ {{ hero.line1 }}</span>
<span class="output">→ {{ hero.line2 }}</span>
<span class="success">✓ {{ hero.line3 }}</span></pre>
        <div class="hero__actions">
          <button type="button" class="btn" @click="goToRegister">$ box21 register</button>
          <button type="button" class="btn btn--dim" @click="goToLogin">$ box21 login</button>
        </div>
      </div>
      <div class="hero__right">
        <div class="terminal-window">
          <div class="terminal-window__bar">
            <span /><span /><span />
            <span class="terminal-window__title">train.log</span>
          </div>
          <pre class="terminal-window__body">Epoch 47/100  loss: 0.0234  mAP: 0.891
Epoch 48/100  loss: 0.0218  mAP: 0.903
Epoch 49/100  loss: 0.0201  mAP: 0.914
<span class="blink">█</span> saving checkpoint → model_v3.weights</pre>
        </div>
      </div>
    </section>

    <section class="modules">
      <div v-for="pillar in pillars" :key="pillar.id" class="module">
        <code class="module__cmd">box21 {{ pillar.id }}</code>
        <h3>{{ pillar.title }}</h3>
        <span v-for="tag in pillar.tags" :key="tag" class="module__flag">--{{ tag.toLowerCase() }}</span>
      </div>
    </section>

    <front-page-design-footer theme="dark" />
  </div>
</template>

<script>
import FrontPageDesignNav from '@/components/front_page/designs/FrontPageDesignNav.vue'
import FrontPageDesignFooter from '@/components/front_page/designs/FrontPageDesignFooter.vue'
import { BRAND, HERO, PILLARS } from '@/components/front_page/designs/shared'

export default {
  name: 'DesignTerminal',
  components: { FrontPageDesignNav, FrontPageDesignFooter },
  data() {
    return { logo: BRAND.logo, hero: HERO, pillars: PILLARS }
  },
  methods: {
    goToLogin() { this.$router.push('/login') },
    goToRegister() { this.$router.push({ path: '/login', query: { tab: 'register' } }) },
  },
}
</script>

<style scoped>
.design-terminal {
  min-height: 100vh;
  background: #0d1117;
  color: #c9d1d9;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
}

.hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  align-items: start;
}

.hero__logo {
  height: 36px;
  filter: brightness(0) invert(1);
  margin-bottom: 32px;
  opacity: 0.9;
}

.hero__code {
  font-size: 0.9rem;
  line-height: 1.8;
  margin: 0 0 32px;
  overflow-x: auto;
}

.comment { color: #8b949e; }
.keyword { color: #7bacd6; }
.output { color: #c9d1d9; }
.success { color: #3fb950; }

.hero__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn {
  background: #0677b9;
  color: #fff;
  border: 1px solid #055a8c;
  padding: 10px 18px;
  font-family: inherit;
  font-size: 0.85rem;
  cursor: pointer;
  border-radius: 6px;
}

.btn--dim {
  background: transparent;
  border-color: #30363d;
  color: #8b949e;
}

.terminal-window {
  border: 1px solid #30363d;
  border-radius: 8px;
  overflow: hidden;
  background: #161b22;
}

.terminal-window__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #21262d;
  border-bottom: 1px solid #30363d;
}

.terminal-window__bar span:first-child,
.terminal-window__bar span:nth-child(2),
.terminal-window__bar span:nth-child(3) {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #484f58;
}

.terminal-window__bar span:first-child { background: #ff5f56; }
.terminal-window__bar span:nth-child(2) { background: #ffbd2e; }
.terminal-window__bar span:nth-child(3) { background: #27c93f; }

.terminal-window__title {
  margin-left: auto !important;
  width: auto !important;
  height: auto !important;
  background: none !important;
  font-size: 0.75rem;
  color: #8b949e;
  border-radius: 0 !important;
}

.terminal-window__body {
  padding: 20px;
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.7;
}

.blink {
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  50% { opacity: 0; }
}

.modules {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 80px;
}

.module {
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 20px;
  background: #161b22;
}

.module__cmd {
  display: block;
  color: #7bacd6;
  font-size: 0.8rem;
  margin-bottom: 12px;
}

.module h3 {
  font-family: system-ui, sans-serif;
  font-size: 1rem;
  margin: 0 0 12px;
  font-weight: 600;
}

.module__flag {
  display: inline-block;
  font-size: 0.7rem;
  color: #8b949e;
  margin-right: 8px;
}

@media (max-width: 768px) {
  .hero { grid-template-columns: 1fr; }
}
</style>
